import type { GanttV5Dependency, GanttV5Link } from './types';

export interface GanttV5ConnectionIndex {
  dependenciesBySourceTaskId: Map<string, GanttV5Dependency[]>;
  dependenciesByTargetTaskId: Map<string, GanttV5Dependency[]>;
  linksByTaskId: Map<string, GanttV5Link[]>;
  linkComponentByTaskId: Map<string, string>;
}

export const buildGanttV5ConnectionIndex = (
  dependencies: GanttV5Dependency[],
  links: GanttV5Link[],
): GanttV5ConnectionIndex => {
  const dependenciesBySourceTaskId = new Map<string, GanttV5Dependency[]>();
  const dependenciesByTargetTaskId = new Map<string, GanttV5Dependency[]>();
  const linksByTaskId = new Map<string, GanttV5Link[]>();
  const parentByTaskId = new Map<string, string>();
  const sizeByRoot = new Map<string, number>();
  const find = (taskId: string): string => {
    const parent = parentByTaskId.get(taskId) ?? taskId;
    if (!parentByTaskId.has(taskId)) {
      parentByTaskId.set(taskId, taskId);
      sizeByRoot.set(taskId, 1);
    }
    if (parent === taskId) return taskId;
    const root = find(parent);
    parentByTaskId.set(taskId, root);
    return root;
  };
  const union = (left: string, right: string) => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot === rightRoot) return;
    const leftSize = sizeByRoot.get(leftRoot) ?? 1;
    const rightSize = sizeByRoot.get(rightRoot) ?? 1;
    if (leftSize < rightSize) {
      parentByTaskId.set(leftRoot, rightRoot);
      sizeByRoot.set(rightRoot, leftSize + rightSize);
      sizeByRoot.delete(leftRoot);
    } else {
      parentByTaskId.set(rightRoot, leftRoot);
      sizeByRoot.set(leftRoot, leftSize + rightSize);
      sizeByRoot.delete(rightRoot);
    }
  };
  dependencies.forEach((dependency) => {
    const values = dependenciesBySourceTaskId.get(dependency.from) ?? [];
    values.push(dependency);
    dependenciesBySourceTaskId.set(dependency.from, values);
    const inbound = dependenciesByTargetTaskId.get(dependency.to) ?? [];
    inbound.push(dependency);
    dependenciesByTargetTaskId.set(dependency.to, inbound);
  });
  links.forEach((link) => {
    const [first, ...rest] = link.taskIds;
    if (first) {
      find(first);
      rest.forEach((taskId) => union(first, taskId));
    }
    link.taskIds.forEach((taskId) => {
      const values = linksByTaskId.get(taskId) ?? [];
      values.push(link);
      linksByTaskId.set(taskId, values);
    });
  });
  const linkComponentByTaskId = new Map<string, string>();
  linksByTaskId.forEach((_value, taskId) => linkComponentByTaskId.set(taskId, find(taskId)));
  return { dependenciesBySourceTaskId, dependenciesByTargetTaskId, linksByTaskId, linkComponentByTaskId };
};

/** Resolve only currently visible related tasks; transitive share components are pre-indexed. */
export const getRelatedVisibleGanttV5TaskIds = (
  index: GanttV5ConnectionIndex,
  activeTaskIds: ReadonlySet<string>,
  visibleTaskIds: ReadonlySet<string>,
): Set<string> => {
  const activeComponents = new Set<string>();
  activeTaskIds.forEach((taskId) => {
    const component = index.linkComponentByTaskId.get(taskId);
    if (component) activeComponents.add(component);
  });
  const related = new Set<string>();
  visibleTaskIds.forEach((taskId) => {
    if (activeTaskIds.has(taskId)) return;
    const component = index.linkComponentByTaskId.get(taskId);
    if (component && activeComponents.has(component)) {
      related.add(taskId);
      return;
    }
    const linkedByDependency = (index.dependenciesBySourceTaskId.get(taskId) ?? [])
      .some((dependency) => activeTaskIds.has(dependency.to))
      || (index.dependenciesByTargetTaskId.get(taskId) ?? [])
        .some((dependency) => activeTaskIds.has(dependency.from));
    if (linkedByDependency) related.add(taskId);
  });
  return related;
};

export const getVisibleGanttV5Connections = (
  index: GanttV5ConnectionIndex,
  visibleTaskIds: ReadonlySet<string>,
): { dependencies: GanttV5Dependency[]; links: GanttV5Link[] } => {
  const visibleDependencies = new Map<string, GanttV5Dependency>();
  visibleTaskIds.forEach((taskId) => {
    for (const dependency of index.dependenciesBySourceTaskId.get(taskId) ?? []) {
      visibleDependencies.set(dependency.id, dependency);
    }
    for (const dependency of index.dependenciesByTargetTaskId.get(taskId) ?? []) {
      visibleDependencies.set(dependency.id, dependency);
    }
  });

  const visibleLinks = new Map<string, GanttV5Link>();
  visibleTaskIds.forEach((taskId) => {
    for (const link of index.linksByTaskId.get(taskId) ?? []) visibleLinks.set(link.id, link);
  });
  return { dependencies: Array.from(visibleDependencies.values()), links: Array.from(visibleLinks.values()) };
};

export const getFocusedGanttV5Connections = (
  connections: { dependencies: GanttV5Dependency[]; links: GanttV5Link[] },
  activeTaskIds: ReadonlySet<string>,
): { dependencies: GanttV5Dependency[]; links: GanttV5Link[] } => {
  if (activeTaskIds.size === 0) return { dependencies: [], links: [] };
  return {
    dependencies: connections.dependencies.filter((dependency) => (
      activeTaskIds.has(dependency.from) || activeTaskIds.has(dependency.to)
    )),
    links: connections.links.filter((link) => (
      link.taskIds.some((taskId) => activeTaskIds.has(taskId))
    )),
  };
};
